<script lang="ts">
import { PropType } from 'vue'
import { uniqueId, isEmpty, debounce } from 'lodash'
import NrFormLabel from '../form-elements/nr-form-label.vue'

interface ValidationProp {
  [key: string]: { message: string; validate(value: string): Promise<boolean> }
}

const COMPONENT_NAME = 'nr_form_input'

export default {
  name: COMPONENT_NAME,
  components: { NrFormLabel },
  emits: ['update:modelValue', 'input', 'keydown', 'blur', 'on-validate'],
  props: {
    help: String,
    hideLabel: { type: Boolean, default: false },
    label: String,
    name: String,
    required: Boolean,
    description: String,
    type: { type: String, default: 'text' },
    maxLength: { type: String, default: '300' },
    minLength: { type: String },
    tag: { type: String, default: 'input' },
    validations: {
      type: Object as PropType<ValidationProp>,
      default() {
        return {}
      }
    },
    iconTooltipText: { type: String },
    withTooltip: { type: Boolean, default: false },
    modelValue: { type: [String, Number, Boolean], default: '' }
  },

  data() {
    return {
      id: uniqueId(`${COMPONENT_NAME}-`),
      errors: {}
    }
  },

  mounted() {
    this.validate = debounce(this.validate, 300)
  },

  methods: {
    validate() {
      Object.keys(this.computedValidations).forEach(async (key) => {
        try {
          const isValid = await this.computedValidations[key].validate(
            this.modelValue
          )

          if (isValid) {
            delete this.errors[key]
          } else {
            throw new Error(`${key} is not valid.`)
          }

          this.$emit('on-validate', true)
        } catch (_error) {
          this.$emit('on-validate', false)
          this.errors = Object.assign({}, this.errors, {
            [`${key}`]: this.computedValidations[key].message
          })
        }
      })
    },

    handleInput(event) {
      this.validate()
      const value = this.isCheckboxOrRadio
        ? event.target.checked
        : event.target.value
      this.$emit('update:modelValue', value)
      this.$emit('input', value)
    },

    handleKeydown(event) {
      this.validate()
      this.$emit('keydown', event)
    },

    handleBlur(event) {
      this.validate()
      this.$emit('blur', event)
    }
  },

  computed: {
    labelClassnames() {
      return [
        'nr-label',
        this.hideLabel ? 'is-sr-only' : '',
        this.required ? 'required-label' : ''
      ]
    },

    inputClassNames(): string[] {
      const classes: string[] = []
      if (this.tag === 'textarea') {
        classes.push('nr-textarea')
      } else {
        classes.push('nr-input')
        switch (typeof this.modelValue) {
          case 'string':
            classes.push(this.modelValue?.length ? 'nr-input--filled' : '')
            break
          case 'number':
            classes.push(this.modelValue > 0 ? 'nr-input--filled' : '')
            break
          case 'boolean':
            classes.push(this.modelValue !== null ? 'nr-input--filled' : '')
            break
          default:
            break
        }
      }
      classes.push(this.errors && !isEmpty(this.errors) ? 'is-danger' : '')
      return classes
    },

    isCheckboxOrRadio(): boolean {
      return this.type === 'checkbox' || this.type === 'radio'
    },

    computedValidations() {
      let validationsComputed = { ...this.validations }
      if (this.required) {
        validationsComputed = Object.assign({}, validationsComputed, {
          required: {
            message: 'This field is required.',
            validate(value?: string) {
              return !!(String(value) || '').trim()
            }
          }
        })
      }

      if (this.minLength) {
        const minLength = this.minLength
        validationsComputed = Object.assign({}, validationsComputed, {
          minLength: {
            message: `Minimum length of ${minLength} required.`,
            validate(value?: string) {
              if (!value) {
                return true
              }

              return value.length >= parseInt(minLength)
            }
          }
        })
      }

      return validationsComputed
    },

    passthroughListeners() {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { input, keydown, blur, ...others } = this.$attrs
      return others
    }
  },

  watch: {
    modelValue() {
      this.validate()
    }
  }
}
</script>

<template>
  <div :class="['nr-field', `field--type-${type}`]">
    <NrFormLabel :class="labelClassnames" :for="id">
      <slot>{{ label }}</slot>
      <template v-if="withTooltip" v-slot:tip>{{ iconTooltipText }}</template>
    </NrFormLabel>

    <template v-if="description">
      <p class="description">{{ description }}</p>
    </template>

    <div class="control">
      <component
        :is="tag"
        v-bind="$attrs"
        :class="inputClassNames"
        :id="id"
        :name="name"
        :aria-required="required"
        :required="required"
        :type="type"
        :maxlength="maxLength"
        :minLength="minLength"
        :rows="tag === 'textarea' ? ($attrs.rows ? $attrs.rows : 4) : ''"
        :value="modelValue"
        v-on="passthroughListeners"
        @input="handleInput"
        @keydown="handleKeydown"
        @blur="handleBlur"
        :checked="isCheckboxOrRadio && !!modelValue"
      />
      <template v-if="help">
        <p class="help">{{ help }}</p>
      </template>

      <template v-if="Object.keys(errors || {}).length">
        <p v-for="(value, key) in errors" :key="key" class="help is-danger">
          {{ value }}
        </p>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
@import '../../styles/tokens/tokens.scss';

.nr-textarea,
.nr-input {
  @include body-default;
  padding: $spacing-4 $spacing-8;
  border: $border-width-default solid $color-purple-80;
  border-radius: $border-radius-default;
  color: $color-purple-110;
  background-color: $color-white;

  &::placeholder {
    color: $color-purple-80;
  }

  &:hover:not(:disabled) {
    border-color: $color-purple-60;
  }

  &--filled:not(:disabled) {
    color: $color-purple-110;
    border-color: $color-purple-110;
  }

  &:active:not(:disabled),
  &:focus-visible:not(:disabled),
  &:focus:not(:disabled) {
    border-color: $color-blue-100;
    outline: none;
  }

  &:focus-visible:not(:disabled) {
    outline: $border-width-medium dotted $color-purple-110;
    outline-offset: $spacing-4;
  }

  &:disabled {
    color: $color-purple-80;
    opacity: $opacity-20;
  }
}

.nr-textarea,
.nr-input[type='email'],
.nr-input[type='text'] {
  width: 100%;
}

.nr-field {
  .nr-label.required-label::after {
    content: ' *';
    padding-left: $spacing-4;
  }
}

.field--type-radio,
.field--type-checkbox {
  display: inline-flex;
  flex-direction: row-reverse;
  align-items: center;

  .nr-label {
    cursor: pointer;
    margin-left: $spacing-8;
  }

  .nr-input:not(:disabled) {
    cursor: pointer;
  }
}

.field--type-checkbox {
  .control {
    display: inline-flex;
    align-items: center;
  }

  .nr-input {
    appearance: none;
    width: $size-xsmall;
    height: $size-xsmall;
    border-width: $border-width-medium;
    color: $color-purple-100;
    border-color: currentColor;
    transition:
      background 150ms ease-out,
      border 150ms ease-out;
    background: transparent;

    &:hover:not(:disabled),
    &:disabled {
      color: $color-purple-80;
      border-color: currentColor;
    }

    &:focus {
      border-color: currentColor;
    }

    &:checked {
      border-color: currentColor;
      // White checkmark, copied from Beufy
      background: currentColor
        url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'%3E%3Cpath style='fill:%23fff' d='M 0.04038059,0.6267767 0.14644661,0.52071068 0.42928932,0.80355339 0.3232233,0.90961941 z M 0.21715729,0.80355339 0.85355339,0.16715729 0.95961941,0.2732233 0.3232233,0.90961941 z'%3E%3C/path%3E%3C/svg%3E")
        no-repeat center center;
    }
  }
}

.field--type-radio {
  .control {
    display: inline-flex;
    align-items: center;
  }

  .nr-input {
    appearance: none;
    width: $size-xsmall;
    height: $size-xsmall;
    border-width: $border-width-medium;
    border-radius: 50%;
    color: $color-purple-100;
    border-color: currentColor;
    transition:
      background 150ms ease-out,
      border 150ms ease-out;
    background: transparent;

    &:hover:not(:disabled),
    &:disabled {
      color: $color-purple-80;
      border-color: currentColor;
    }

    &:focus {
      border-color: currentColor;
    }

    &:checked {
      border-color: currentColor;
      // Url-encode the hex color.
      $fill: '%23' + str-slice(#{$color-purple-100}, 2);
      // Make the background a circle, with the fill color from above
      background: url("data:image/svg+xml, %3Csvg viewBox='0 0 1 1' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle r='0.2' cx='.5' cy='.5' fill='#{$fill}' /%3E%3C/svg%3E ")
        no-repeat center center;
    }
  }
}

.nr-field {
  .description {
    @include body-small;
  }
}
</style>
